import type {
    OfferValidationResultDTO,
    PurchaseAmountDTO,
    TestAB,
    ValidationDataDTO,
} from './dto-client'
import type { PaymentPlan } from './PaymentPlan'

export interface OfferValidation {
    applicationId: number
    status: boolean
    validatedPaymentPlan: PaymentPlan
    hasPossibleCashFlows: boolean
    paymentPlans: PaymentPlan[]
    availableExposure: number
    availableExposureCpiIncluded: number
    recommendedPurchaseAmount: number
    testGroup: TestAB | null
    failedReasonType?: 'NO_PAYMENT_OPTIONS' | 'FAIL_CREDIT_CHECK' | 'EMPTY'
    shadowLimit?: number
    purchaseAmount?: PurchaseAmountDTO
}

/**
 * Maps the ValidationDataDTO wire shape to the normalized OfferValidation.
 * Sorts the payment-plan list by descending term-maturity months.
 * Replaces the former OfferValidation class.
 */
export const toOfferValidation = (data: ValidationDataDTO): OfferValidation => ({
    applicationId: data.applicationId,
    status: data.status,
    validatedPaymentPlan: data.validatedPaymentPlan,
    hasPossibleCashFlows: data.hasPossibleCashFlows,
    paymentPlans: [...data.paymentPlans].sort((a, b) =>
        a.termMaturity.numberOfMonths > b.termMaturity.numberOfMonths ? -1 : 1,
    ),
    availableExposure: data.availableExposure,
    availableExposureCpiIncluded: data.availableExposureCpiIncluded,
    recommendedPurchaseAmount: data.recommendedPurchaseAmount,
    testGroup: data.testGroup,
    failedReasonType: data.failedReasonType,
    shadowLimit: data.shadowLimit,
    purchaseAmount: data.purchaseAmount,
})

/**
 * Overlays the figures the backend confirmed during validation onto the plan the customer chose at
 * the plan-data step.
 *
 * The two sources are complementary and neither is sufficient alone: the validated-offer read
 * carries the confirmed money but no maturity identifiers, while the chosen plan carries the
 * identifiers the contract draft and the signing submit need but only pre-scoring figures. Four
 * fields are named differently on the two sides — `terms`, `interestsAndFees`, `total` and
 * `insurancePremium` — which is why this mapping is explicit rather than a spread.
 *
 * `firstInstallmentDate` deliberately stays the chosen plan's: the read's `firstPaymentDate` is a
 * date-part array rather than an epoch, and the plan identity is the frontend's to hold.
 */
export const toConfirmedPaymentPlan = (
    chosenPlan: PaymentPlan,
    confirmed: OfferValidationResultDTO,
): PaymentPlan => ({
    ...chosenPlan,
    loanAmount: confirmed.loanAmount,
    installmentAmount: confirmed.installmentAmount,
    numberOfInstallments: confirmed.terms,
    eom: confirmed.eom,
    interestAndFees: confirmed.interestsAndFees,
    totalAmount: confirmed.total,
    totalLoanAmount: confirmed.totalLoanAmount,
    insurancePremiumFee: confirmed.insurancePremium,
    nominalInterestRate: confirmed.nominalInterestRate,
    // Omitted by markets whose validation step does not persist a risk cost — fall back to the
    // chosen plan so the confirmation screen never renders undefined.
    costOfRisk: confirmed.costOfRisk ?? chosenPlan.costOfRisk,
    calculationMethod: confirmed.calculationMethod ?? chosenPlan.calculationMethod,
})
